/**
 * useTokenManager.js — Token Lifecycle Hook
 *
 * จัดการ:
 *   1. Proactive Refresh Timer  — refresh อัตโนมัติก่อน token หมด 2 นาที
 *   2. Heartbeat               — ping last_active ทุก 5 นาที
 *   3. Visibility API          — ตรวจ token เมื่อ user กลับมาที่ tab
 *   4. Online / Offline        — pause heartbeat เมื่อ offline
 *
 * ไม่มี state ของตัวเอง — รับ callbacks จาก AuthContext
 */

import { useCallback, useEffect, useRef } from 'react';
import { authService } from '../services/authService';
import { refreshWithCrossTabLock } from '../lib/authClient';

// ─── Constants ────────────────────────────────────────────────────────────────

const TOKEN_REFRESH_BUFFER = 2 * 60 * 1000;  // refresh ก่อนหมด 2 นาที
const TOKEN_EXPIRY_MARGIN  = 30 * 1000;       // ถือว่าหมดก่อนจริง 30 วินาที
const HEARTBEAT_INTERVAL   = 5 * 60 * 1000;  // ping ทุก 5 นาที
const VISIBILITY_DEBOUNCE  = 800;             // debounce tab-visible event

// ─── Pure Helpers ─────────────────────────────────────────────────────────────

const decodeJwt = (token) => {
  try { return JSON.parse(atob(token.split('.')[1])); }
  catch { return null; }
};

const isTokenExpired = (token) => {
  const p = decodeJwt(token);
  if (!p?.exp) return false;
  return Date.now() >= (p.exp * 1000 - TOKEN_EXPIRY_MARGIN);
};

const msUntilRefresh = (token) => {
  const p = decodeJwt(token);
  if (!p?.exp) return null;
  return (p.exp * 1000 - TOKEN_REFRESH_BUFFER) - Date.now();
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * @param {object} opts
 * @param {(tokens: {access_token: string, refresh_token: string|null}) => void} opts.onTokensRefreshed
 * @param {(reason?: string) => void} opts.onForceLogout
 */
export const useTokenManager = ({ onTokensRefreshed, onForceLogout }) => {
  const proactiveTimerRef  = useRef(null);
  const heartbeatTimerRef  = useRef(null);
  const visibilityDebounce = useRef(null);
  const isOnlineRef        = useRef(navigator.onLine);

  // ─── Internal: Silent Refresh ─────────────────────────────────────────────
  const doSilentRefresh = useCallback(async () => {
    const rt = localStorage.getItem('refreshToken');
    if (!rt) { onForceLogout(); return null; }

    try {
      const { data } = await refreshWithCrossTabLock(rt);
      if (data.error_code) throw Object.assign(new Error(data.message), { error_code: data.error_code });

      const { access_token, refresh_token: newRt } = data;
      localStorage.setItem('token', access_token);
      if (newRt) localStorage.setItem('refreshToken', newRt);

      onTokensRefreshed({ access_token, refresh_token: newRt });
      console.log('✅ Token refreshed silently');
      return access_token;

    } catch (err) {
      console.error('❌ Silent refresh failed:', err.message);
      onForceLogout(err.error_code || null);
      return null;
    }
  }, [onTokensRefreshed, onForceLogout]);

  // ─── Proactive Refresh Timer ──────────────────────────────────────────────
  const clearProactiveTimer = useCallback(() => {
    if (proactiveTimerRef.current) {
      clearTimeout(proactiveTimerRef.current);
      proactiveTimerRef.current = null;
    }
  }, []);

  const scheduleProactiveRefresh = useCallback((accessToken) => {
    clearProactiveTimer();

    const delay = msUntilRefresh(accessToken);
    if (delay === null) return; // ไม่มี exp → ข้าม

    if (delay <= 0) {
      // Token ใกล้หมดหรือหมดแล้ว → refresh ทันที
      console.warn('⏰ Token near expiry → refreshing now');
      doSilentRefresh().then((newToken) => {
        if (newToken) scheduleProactiveRefresh(newToken);
      });
      return;
    }

    const min = Math.floor(delay / 60000);
    const sec = Math.floor((delay % 60000) / 1000);
    console.log(`⏰ Proactive refresh scheduled in ${min}m ${sec}s`);

    proactiveTimerRef.current = setTimeout(async () => {
      console.log('⏰ Proactive refresh — time!');
      const newToken = await doSilentRefresh();
      if (newToken) scheduleProactiveRefresh(newToken);
    }, delay);
  }, [clearProactiveTimer, doSilentRefresh]);

  // ─── Heartbeat ────────────────────────────────────────────────────────────
  const startHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);

    heartbeatTimerRef.current = setInterval(async () => {
      if (!isOnlineRef.current) return; // offline → ข้าม
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const rt = localStorage.getItem('refreshToken');
        await authService.heartbeat(rt);
        console.log('💓 Heartbeat: last_active updated');
      } catch (err) {
        // ไม่ critical — interceptor จัดการ 401 เอง
        if (err.response?.status !== 401) {
          console.warn('💓 Heartbeat failed (non-critical):', err.message);
        }
      }
    }, HEARTBEAT_INTERVAL);
  }, []);

  // ─── Stop Everything ──────────────────────────────────────────────────────
  const stopAll = useCallback(() => {
    clearProactiveTimer();
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
    if (visibilityDebounce.current) {
      clearTimeout(visibilityDebounce.current);
      visibilityDebounce.current = null;
    }
  }, [clearProactiveTimer]);

  // ─── Visibility API: ตรวจ token เมื่อกลับมาที่ tab ──────────────────────
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;

      // Debounce ป้องกัน fire ซ้ำ
      if (visibilityDebounce.current) clearTimeout(visibilityDebounce.current);
      visibilityDebounce.current = setTimeout(() => {
        const token = localStorage.getItem('token');
        const rt    = localStorage.getItem('refreshToken');
        if (!token || !rt) return;

        if (isTokenExpired(token)) {
          console.log('👁️ Tab visible — token expired → refreshing');
          doSilentRefresh().then((newToken) => {
            if (newToken) scheduleProactiveRefresh(newToken);
          });
        } else {
          // Token OK — reset timer ให้ accurate หลัง browser throttle
          console.log('👁️ Tab visible — token OK → resetting timer');
          scheduleProactiveRefresh(token);
        }
      }, VISIBILITY_DEBOUNCE);
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [doSilentRefresh, scheduleProactiveRefresh]);

  // ─── Online / Offline ─────────────────────────────────────────────────────
  useEffect(() => {
    const onOnline = () => {
      isOnlineRef.current = true;
      console.log('🌐 Online — checking token...');
      const token = localStorage.getItem('token');
      const rt    = localStorage.getItem('refreshToken');
      if (!token || !rt) return;

      if (isTokenExpired(token)) {
        doSilentRefresh().then((newToken) => {
          if (newToken) scheduleProactiveRefresh(newToken);
        });
      } else {
        scheduleProactiveRefresh(token);
      }
    };

    const onOffline = () => {
      isOnlineRef.current = false;
      console.warn('🌐 Offline — heartbeat paused');
    };

    window.addEventListener('online',  onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online',  onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [doSilentRefresh, scheduleProactiveRefresh]);

  return { scheduleProactiveRefresh, startHeartbeat, stopAll };
};
